import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from '@/components/atoms/Button';
import { Text } from '@/components/atoms/Text';
import PasswordInput from '@/components/atoms/Form/PasswordInput/PasswordInput';
import ErrorMessage from '@/components/atoms/ErrorMessage';
import { SVGIcon } from '@/components/atoms/Icon/SVGIcon';
import { SVGIcons } from '@/components/atoms/Icon/SVGIcon.interface';
import { AlertContainer } from '@/components/organisms/ValidatePropagation/ValidatePropagation.style';
import { computeText } from '@/locales/utils';
import { PasswordPolicy } from '@/services/innovorder/user/user.type';
import { Center, FormContainer, FormTitle, InputSpacer, MarginTop } from '../../Login/LoginPanel/LoginPanel.style';

type ActivateAccountPanelProps = {
    onSubmit: (payload: { password: string; confirmPassword: string }) => void;
    errorMessage: string;
    isLoading: boolean;
    passwordPolicy?: PasswordPolicy;
    submitError?: string;
    isSubmitting?: boolean;
    canRenew?: boolean;
    onResend?: () => void;
    isRenewing?: boolean;
    renewSuccess?: boolean;
};

const ActivateAccountPanel: React.FunctionComponent<React.PropsWithChildren<ActivateAccountPanelProps>> = ({
    onSubmit,
    errorMessage,
    isLoading,
    passwordPolicy,
    submitError,
    isSubmitting = false,
    canRenew = false,
    onResend,
    isRenewing = false,
    renewSuccess = false,
}) => {
    const intl = useIntl();

    const validationSchema = React.useMemo(() => {
        let passwordRules = Yup.string().required(computeText(intl, 'field.required'));

        if (passwordPolicy?.minLength) {
            passwordRules = passwordRules.min(
                passwordPolicy.minLength,
                computeText(intl, 'activateAccount.error.password.minLength', {
                    min: passwordPolicy.minLength,
                }),
            );
        } else {
            passwordRules = passwordRules.min(12, computeText(intl, 'user.create.error.password.min'));
        }

        if (passwordPolicy?.maxLength) {
            passwordRules = passwordRules.max(
                passwordPolicy.maxLength,
                computeText(intl, 'activateAccount.error.password.maxLength', {
                    max: passwordPolicy.maxLength,
                }),
            );
        }

        if (passwordPolicy?.requireUppercase) {
            passwordRules = passwordRules.matches(
                /[A-Z]/,
                computeText(intl, 'activateAccount.error.password.requireUppercase'),
            );
        }

        if (passwordPolicy?.requireLowercase) {
            passwordRules = passwordRules.matches(
                /[a-z]/,
                computeText(intl, 'activateAccount.error.password.requireLowercase'),
            );
        }

        if (passwordPolicy?.requireNumbers) {
            passwordRules = passwordRules.matches(
                /[0-9]/,
                computeText(intl, 'activateAccount.error.password.requireNumbers'),
            );
        }

        if (passwordPolicy?.requireSpecialCharacters) {
            passwordRules = passwordRules.matches(
                /[^a-zA-Z0-9]/,
                computeText(intl, 'activateAccount.error.password.requireSpecialCharacters'),
            );
        }

        return Yup.object().shape({
            password: passwordRules,
            confirmPassword: Yup.string()
                .oneOf(
                    [Yup.ref('password'), null],
                    computeText(intl, 'user.create.error.passwordConfirm.sameAsPassword'),
                )
                .required(computeText(intl, 'field.required')),
        });
    }, [passwordPolicy, intl]);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    const submit = (data: { password: string; confirmPassword: string }) => {
        onSubmit(data);
    };

    if (isLoading) {
        return (
            <div>
                <Center>
                    <FormContainer>
                        <FormTitle>
                            <FormattedMessage id="activateAccount.title" />
                        </FormTitle>
                        <Text text="activateAccount.loading" />
                    </FormContainer>
                </Center>
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div>
                <Center>
                    <FormContainer>
                        <FormTitle>
                            <FormattedMessage id="activateAccount.title" />
                        </FormTitle>
                        {!renewSuccess && (
                            <>
                                <ErrorMessage>
                                    <FormattedMessage id={errorMessage} />
                                </ErrorMessage>
                                {canRenew && onResend && (
                                    <MarginTop>
                                        <Button
                                            type="button"
                                            buttonType="primary"
                                            fullWidth
                                            thick
                                            onClick={onResend}
                                            disabled={isRenewing}
                                        >
                                            {isRenewing
                                                ? computeText(intl, 'activateAccount.resending')
                                                : computeText(intl, 'activateAccount.resend')}
                                        </Button>
                                    </MarginTop>
                                )}
                            </>
                        )}
                        {renewSuccess && (
                            <AlertContainer status="info">
                                <Text text="activateAccount.renewSuccess" />
                                <SVGIcon icon={SVGIcons.CHECK} size={24} />
                            </AlertContainer>
                        )}
                    </FormContainer>
                </Center>
            </div>
        );
    }

    return (
        <div>
            <Center>
                <FormContainer onSubmit={handleSubmit(submit)}>
                    <FormTitle>
                        <FormattedMessage id="activateAccount.title" />
                    </FormTitle>
                    <InputSpacer>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <PasswordInput
                                    value={value}
                                    onChange={onChange}
                                    placeholder="activateAccount.password.placeholder"
                                />
                            )}
                        />
                        {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
                    </InputSpacer>
                    <InputSpacer>
                        <Controller
                            name="confirmPassword"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <PasswordInput
                                    value={value}
                                    onChange={onChange}
                                    placeholder="activateAccount.confirmPassword.placeholder"
                                />
                            )}
                        />
                        {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
                    </InputSpacer>
                    {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
                    <MarginTop>
                        <Button type="submit" buttonType="primary" fullWidth thick disabled={isSubmitting}>
                            <Text text={isSubmitting ? 'activateAccount.submitting' : 'activateAccount.submit'} />
                        </Button>
                    </MarginTop>
                </FormContainer>
            </Center>
        </div>
    );
};

export default ActivateAccountPanel;
